import { BSC_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { NODE_SALE_V1_SC_ADDRESS } from '@/global/configs/common.config';
import { ETH_DECIMAL, USD_VALUE_DECIMAL } from '@/global/configs/decimal.config';
import { TOKEN_CONFIG } from '@/global/constants/payment.constant';
import { CoinSymbol } from '@/global/enums/common.enums';
import type { SignPriceSchemaType } from '@/global/schemas/price.schema';
import { useGA, useWeb3 } from '@/hooks';
import { useRequestPriceV1Mutation } from '@/store/services/price.service';
import { useAppSelector } from '@/store/utils';
import { logError } from '@/utils/helpers';

import type { ApproveCallbackParams, BuyCallbackParams, BuyNodeV1Hook } from '../types/buyNode.type';

const COIN_APPROVE_MAX = [CoinSymbol.USDC, CoinSymbol.USDT];
const MAXIMUM_APPROVE_AMOUNT = '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';

const useBuyNodeV1 = (): BuyNodeV1Hook => {
  const { value: isExecuting, setTrue: setIsExecutingTrue, setFalse: setIsExecutingFalse } = useBoolean(false);
  const { address, config } = useWeb3();
  const { trackButtonClickEvent, trackGAEvent } = useGA();
  const [requestPriceV1] = useRequestPriceV1Mutation();
  const { nodeId, referralCode } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);

  const handleBuyTransaction = async (
    currency: CoinSymbol,
    usdAmount: number,
    signatureData: SignPriceSchemaType,
    count: number,
    nft: string,
  ) => {
    try {
      const {
        price,
        decimals,
        deadline,
        nonce,
        signature: { r, s, v },
      } = signatureData;

      const baseArgs = {
        nft,
        amount: count,
        buyer: address,
        price,
        decimals,
        deadline,
        v,
        r,
        signature: s,
        nonce,
      };

      const value =
        currency === CoinSymbol.BSC
          ? BigInt(Math.ceil((usdAmount * 10 ** (ETH_DECIMAL + decimals) * 1.03) / price))
          : BigInt(0);

      return await writeContract(config, {
        abi: BSC_ABI,
        address: NODE_SALE_V1_SC_ADDRESS,
        functionName: currency === CoinSymbol.BSC ? `buyByETH` : `buyBy${TOKEN_CONFIG[currency].name}`,
        args: [Math.ceil(usdAmount * 1.008 * 10 ** USD_VALUE_DECIMAL), baseArgs],
        value,
      });
    } catch (error: unknown) {
      const errorDetails = logError(error, `handleBuyTransaction-${currency}`);
      const errorMessage =
        errorDetails.message || (error instanceof Error ? error.message : `Buy transaction failed for ${currency}`);

      // Re-throw the error with more context
      throw new Error(`Transaction failed: ${errorMessage}`, { cause: error });
    }
  };

  const handleBuyCreditCard = async ({
    amount,
    token,
    isCrossmint,
    receivedAddress,
  }: BuyCallbackParams & { token: CoinSymbol }) => {
    try {
      const signatureRes = await requestPriceV1({
        amount,
        buyerAddress: receivedAddress,
        coin: TOKEN_CONFIG[token].key,
        sale: nodeId,
        nft: nodeId,
        referralCode: referralCode ? referralCode : undefined,
        isCrossmint,
      });
      if (
        !signatureRes.data ||
        signatureRes.data?.remain <= 0 ||
        (signatureRes.data?.remain && signatureRes.data?.remain < amount)
      ) {
        toast.error('You amount is over exceed limit of project allow each address to buy');
        return;
      }
      if (!signatureRes.data.signature) {
        toast.error('Signature is invalid. Please try again!');
        return;
      }
      return signatureRes.data;
    } catch (error) {
      const errorDetails = logError(error, `handleBuy-${token}`);
      const errorMessage =
        errorDetails.message || (error instanceof Error ? error.message : 'Buy Node Sale failed, please try again');
      toast.error(errorMessage);
    }
  };

  const handleBuy = useCallback(
    async ({ usdAmount, amount, token, onSuccess, onError }: BuyCallbackParams & { token: CoinSymbol }) => {
      if (!nodeId || !address) return;
      setIsExecutingTrue();
      trackButtonClickEvent({ name: 'buy', address });

      try {
        const signatureRes = await requestPriceV1({
          amount,
          buyerAddress: address,
          coin: TOKEN_CONFIG[token].key,
          nft: nodeId,
          ...(referralCode !== '' && { referralCode }),
        });

        if (
          !signatureRes.data ||
          signatureRes.data?.remain <= 0 ||
          (signatureRes.data?.remain && signatureRes.data?.remain < amount)
        ) {
          toast.error('You amount is over exceed limit of project allow each address to buy');
          return;
        }

        if (!signatureRes.data.signature) {
          toast.error('Signature is invalid. Please try again!');
          return;
        }

        const hash = await handleBuyTransaction(token, usdAmount, signatureRes.data, amount, nodeId);
        toast(TransactionSubmittedToast());
        const receipt = await waitForTransactionReceipt(config, { hash });
        trackGAEvent({
          action: 'purchase',
          category: projectInfo?.node?.name,
          label: 'purchase success',
          value: usdAmount,
          transaction_id: receipt.transactionHash,
          fields: {
            transaction_id: receipt.transactionHash,
            method: token,
            buyer: address,
            currency: 'usd',
            payment_type: token,
            items: [
              {
                item_id: projectInfo?.nft,
                item_name: projectInfo?.node?.name,
                item_category: projectInfo?.node?.name,
                quantity: amount,
                price: usdAmount / amount,
              },
            ],
          },
        });
        onSuccess?.(receipt.transactionHash);
      } catch (error: unknown) {
        const errorDetails = logError(error, `handleBuy-${token}`);
        const errorMessage =
          errorDetails.message || (error instanceof Error ? error.message : 'Buy Node Sale failed, please try again');

        toast.error(errorMessage);
        onError?.(errorMessage);
        throw error;
      } finally {
        setIsExecutingFalse();
      }
    },
    [
      config,
      address,
      nodeId,
      projectInfo,
      referralCode,
      requestPriceV1,
      setIsExecutingTrue,
      setIsExecutingFalse,
      trackGAEvent,
      trackButtonClickEvent,
    ],
  );

  const handleApprove = useCallback(
    async (currency: CoinSymbol, { priceInUsd, tokenPrice, onSuccess, onError }: ApproveCallbackParams) => {
      if (currency === CoinSymbol.BSC) {
        onSuccess?.();
        return;
      }

      const tokenConfig = TOKEN_CONFIG[currency];
      try {
        setIsExecutingTrue();
        trackButtonClickEvent({ name: 'approve', address });
        const amount =
          COIN_APPROVE_MAX.includes(currency) && priceInUsd !== 0
            ? MAXIMUM_APPROVE_AMOUNT
            : Math.ceil(Number(((priceInUsd * 1.008) / tokenPrice) * 1.1)) * 10 ** tokenConfig.decimals;

        const hash = await writeContract(config, {
          abi: tokenConfig.abi,
          address: tokenConfig.address as `0x${string}`,
          functionName: 'approve',
          args: [NODE_SALE_V1_SC_ADDRESS, amount],
          value: BigInt(0),
        });
        await waitForTransactionReceipt(config, { hash });
        toast.success(`Approve ${currency} successfully`);
        onSuccess?.();
      } catch (error: unknown) {
        console.log('🚀 ~ error:', error);
        const errorDetails = logError(error, `handleApprove-${currency}`);
        const errorMessage =
          errorDetails.message || (error instanceof Error ? error.message : `Approve ${currency} failed`);
        onError?.();
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsExecutingFalse();
      }
    },
    [config, setIsExecutingTrue, setIsExecutingFalse, trackButtonClickEvent],
  );

  return {
    handleBuy,
    handleApprove,
    handleBuyOTC: () => {},
    isExecuting,
    handleBuyCreditCard,
  };
};

export { useBuyNodeV1 };
